<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';

import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import CsatContactCell from './CsatContactCell.vue';
import CsatExpandedRow from './CsatExpandedRow.vue';
import CsatEmptyState from './CsatEmptyState.vue';
import CsatTableLoader from './CsatTableLoader.vue';

import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { pageIndex } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange', 'editorState']);
const { t } = useI18n();
const { isCloudFeatureEnabled, isOnChatwootCloud } = useAccount();
const csatResponses = useMapGetter('csat/getCSATResponses');

const isFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('csat_review_notes')
);
const showExpandableRows = computed(
  () => isFeatureEnabled.value || isOnChatwootCloud.value
);
const metrics = useMapGetter('csat/getMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);

const expandedRows = ref({});
const openedRows = ref({});

const toggleRow = id => {
  openedRows.value[id] = true;
  expandedRows.value = {
    ...expandedRows.value,
    [id]: !expandedRows.value[id],
  };
};

const isRowExpanded = id => !!expandedRows.value[id];

const tableData = computed(() => {
  return csatResponses.value.map(response => ({
    id: response.id,
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    feedbackText: response.feedback_message || '',
    conversationId: response.conversation_id,
    csatReviewNotes: response.csat_review_notes,
    createdAgo: dynamicTime(response.created_at),
    createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
    _original: response,
  }));
});

const getRatingData = rating => {
  return CSAT_RATINGS.find(r => r.value === rating) || {};
};

const columnHelper = createColumnHelper();

const columns = computed(() => {
  const baseColumns = [
    columnHelper.accessor('contact', {
      header: t('CSAT_REPORTS.TABLE.HEADER.CONTACT_NAME'),
    }),
    columnHelper.accessor('rating', {
      header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
      size: 120,
    }),
    columnHelper.accessor('feedbackText', {
      header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
      size: 500,
    }),
    columnHelper.accessor('assignedAgent', {
      header: t('CSAT_REPORTS.TABLE.HEADER.HANDLED_BY'),
      size: 160,
    }),
  ];

  if (showExpandableRows.value) {
    baseColumns.push(
      columnHelper.accessor('actions', {
        header: '',
        size: 50,
      })
    );
  }

  return baseColumns;
});

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return metrics.value.totalResponseCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-hidden"
  >
    <CsatTableLoader v-if="isLoading" />

    <div v-else-if="tableData.length" class="md:overflow-x-auto">
      <table class="block w-full md:table">
        <thead class="hidden md:table-header-group bg-n-solid-2 border-b border-n-container">
          <tr>
            <th
              v-for="header in table.getFlatHeaders()"
              :key="header.id"
              :style="{
                width: header.getSize() ? `${header.getSize()}px` : 'auto',
              }"
              class="text-left py-3 px-5 font-medium text-sm text-n-slate-12"
            >
              {{ header.column.columnDef.header }}
            </th>
          </tr>
        </thead>
        <tbody class="block divide-y divide-n-container md:table-row-group">
          <template v-for="row in tableData" :key="row.id">
            <tr
              class="group flex min-w-0 flex-col gap-3 p-4 hover:bg-n-slate-2 dark:hover:bg-n-solid-3 transition-colors md:table-row md:p-0"
              :class="{
                'bg-n-slate-2 dark:bg-n-solid-3': isRowExpanded(row.id),
                'cursor-pointer': showExpandableRows,
              }"
              @click="showExpandableRows && toggleRow(row.id)"
            >
              <td class="block min-w-0 break-words md:table-cell md:py-4 md:px-5">
                <CsatContactCell
                  :contact="row.contact"
                  :conversation-id="row.conversationId"
                  :created-ago="row.createdAgo"
                  :created-at="row.createdAt"
                />
              </td>
              <td class="block min-w-0 break-words md:table-cell md:py-4 md:px-5">
                <span class="mb-1 block text-xs text-n-slate-11 md:hidden">{{ $t('CSAT_REPORTS.TABLE.HEADER.RATING') }}</span>
                <div
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded-xl"
                  :style="{
                    backgroundColor: `${getRatingData(row.rating).color}20`,
                  }"
                >
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ $t(getRatingData(row.rating).translationKey) }}
                  </span>
                </div>
              </td>
              <td class="block min-w-0 break-words md:table-cell md:py-4 md:px-5">
                <span class="mb-1 block text-xs text-n-slate-11 md:hidden">{{ $t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT') }}</span>
                <span
                  v-if="!row.feedbackText"
                  class="text-n-slate-10 italic text-sm"
                >
                  {{ $t('CSAT_REPORTS.NO_FEEDBACK') }}
                </span>
                <div v-else class="text-sm text-n-slate-12">
                  <ShowMore :text="row.feedbackText" :limit="100" />
                </div>
              </td>
              <td class="block min-w-0 break-words md:table-cell md:py-4 md:px-5">
                <span class="mb-1 block text-xs text-n-slate-11 md:hidden">{{ $t('CSAT_REPORTS.TABLE.HEADER.HANDLED_BY') }}</span>
                <UserAvatarWithName
                  v-if="row.assignedAgent"
                  :user="row.assignedAgent"
                  :size="28"
                />
                <span v-else class="text-n-slate-10 text-sm italic">
                  {{ $t('CSAT_REPORTS.NO_AGENT') }}
                </span>
              </td>
              <td v-if="showExpandableRows" class="block min-w-0 break-words md:table-cell md:py-4 md:px-5">
                <button
                  type="button"
                  class="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl bg-n-alpha-2 px-3 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :aria-expanded="isRowExpanded(row.id)"
                  :aria-label="$t(isRowExpanded(row.id) ? 'CSAT_REPORTS.CLOSE_REVIEW' : 'CSAT_REPORTS.OPEN_REVIEW')"
                  @click.stop="toggleRow(row.id)"
                >
                  <span class="md:hidden">{{ $t(isRowExpanded(row.id) ? 'CSAT_REPORTS.CLOSE_REVIEW' : 'CSAT_REPORTS.OPEN_REVIEW') }}</span>
                  <i
                    class="size-4 block transition-transform duration-200"
                    :class="
                      isRowExpanded(row.id)
                        ? 'i-ri-arrow-up-s-line'
                        : 'i-ri-arrow-down-s-line'
                    "
                  />
                </button>
              </td>
            </tr>
            <tr
              v-if="showExpandableRows && openedRows[row.id]"
              v-show="isRowExpanded(row.id)"
              class="block !border-t-0 md:table-row"
            >
              <td colspan="5" class="block p-0 !border-t-0 md:table-cell">
                <CsatExpandedRow :response="row._original" @editor-state="emit('editorState', { id: row.id, ...$event })" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <CsatEmptyState
      v-else
      :title="$t('CSAT_REPORTS.NO_RECORDS')"
      :description="$t('CSAT_REPORTS.NO_RECORDS_DESCRIPTION')"
    />

    <div
      v-if="metrics.totalResponseCount"
      class="px-6 py-4 border-t border-n-weak"
    >
      <Pagination :table="table" />
    </div>
  </div>
</template>
